import { IRowCells, ITableData } from 'types/excelExport';
import { ColSortConfig, SortDirection } from 'types/sort';
import {
    sentenceToSnakeCase, numeralFormatterCurrency, formatDecimalAsPercentage, numeralFormatter,
} from 'ns_libs/formatter';
import { AttributeKey } from 'views/Reports/types';
import { UNIT_OF_MEASURE_TYPES, UNIT_TYPE_NAMES } from 'constants/unitTypes';
import { Attributes, dollarValueAttributes, percentageValueAttributes } from './ConfigurationSidebar/constants';

const EXCEL_NSBLUE = 'e6e8e8';
const EXCEL_WHITE = '0FB3FF';
const BORDER_COLOR = '000000';

const excelPercentFormat = '0.00%';
const excelUSDFormat = '$#,##0.00';
const defaultFontSize = 12;
const borderStyle = { style: 'medium', color: { argb: BORDER_COLOR } };

export const formatExcelExportData = (title: string, subtitle: string, data: {
    id: number;
    values: string[];
}[], columns: AttributeKey[]) => {
    const topHeaderRows = [
        {
            rowFont: { bold: true, size: defaultFontSize },
            rowCells: [{ value: title }],
        },
        {
            rowFont: { bold: true, size: defaultFontSize },
            rowCells: [{ value: subtitle }],
        },
    ];

    const tableHeaderLabelsRow = {
        rowFont: {
            bold: true,
            size: 12,
            color: { argb: EXCEL_NSBLUE },
        },
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_WHITE },
        },
        rowCells: columns.map((col, idx) => ({
            value: col,
            width: 20,
            border: {
                top: borderStyle,
                left: idx === 0 ? borderStyle : undefined,
                right: idx === columns.length - 1 ? borderStyle : undefined,
            },
        })),
    };

    const rows = data.map((deal, dealIdx) => {
        const newRow: ITableData = {
            rowCells: [],
        };
        deal.values.forEach((cell: string, idx: number) => {
            const newCell: IRowCells = {
                value: (() => {
                    if (cell === null || cell === undefined || cell === '') {
                        return '-';
                    }
                    if (Array.isArray(cell)) {
                        return cell.map(v => (v === '' ? '-' : v)).join('\n');
                    }
                    return cell;
                })(),
                border: {
                    left: idx === 0 ? borderStyle : undefined,
                    right: idx === deal.values.length - 1 ? borderStyle : undefined,
                    bottom: dealIdx === data.length - 1 ? borderStyle : undefined,
                },
            };
            if (dollarValueAttributes.includes(columns[idx] as Attributes)) newCell.numFmt = excelUSDFormat;
            if (percentageValueAttributes.includes(columns[idx] as Attributes)) newCell.numFmt = excelPercentFormat;
            return newRow.rowCells.push(newCell);
        });
        return newRow;
    });

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [tableHeaderLabelsRow],
                tableData: rows,
                tableFooter: {
                    rowCells: [],
                },
            },
        ],
    };
};

export const formatPipelineDealsTableData = (
    data: any[],
    selectedColumns: string[],
    colSortConfig: ColSortConfig<AttributeKey> | undefined,
): { id: number; values: string[] }[] => {
    const selectedColumnsData = data.map(row => {
        const formattedRow: { id: number; values: string[] } = {
            id: row.deal_id,
            values: [],
        };

        selectedColumns.forEach((col: string) => {
            let formattedValue = '';
            const colNameTrimmed = col.trim();
            const snakeCaseColName = convertColNameToSnakeCaseKeys(colNameTrimmed);

            if (percentageValueAttributes.includes(colNameTrimmed as Attributes)) {
                formattedValue = row[snakeCaseColName] !== null && row[snakeCaseColName] !== undefined ? `${row[snakeCaseColName]}%` : '';
            } else if (dollarValueAttributes.includes(colNameTrimmed as Attributes)) {
                formattedValue = row[snakeCaseColName] !== null && row[snakeCaseColName] !== undefined
                    ? numeralFormatterCurrency(row[snakeCaseColName]) : '';
            } else if (colNameTrimmed === Attributes.DateCreated) {
                formattedValue = row[snakeCaseColName] ? new Date(row[snakeCaseColName]).toISOString().split('T')[0] : '';
            } else if (colNameTrimmed === Attributes.IRR) {
                formattedValue = row[snakeCaseColName] !== null && row[snakeCaseColName] !== undefined
                    ? formatDecimalAsPercentage(row[snakeCaseColName]) : '';
            } else if (colNameTrimmed === Attributes.EquityMultiple) {
                formattedValue = row[snakeCaseColName] !== null && row[snakeCaseColName] !== undefined
                    ? `${row[snakeCaseColName]}x` : '';
            } else if (colNameTrimmed === Attributes.Units) {
                formattedValue = row[snakeCaseColName]?.map((item: { unit: string, value: number }) => {
                    const unitType = UNIT_TYPE_NAMES[item.unit as UNIT_OF_MEASURE_TYPES].labelName;
                    const formattedNumber = Number.isInteger(item.value)
                        ? numeralFormatter(item.value, '0,0')
                        : numeralFormatter(item.value, '0,0.00');
                    return `${unitType}: ${formattedNumber}`;
                });
            } else if (row.custom_attributes && !Object.values(Attributes).includes(colNameTrimmed as Attributes)) {
                formattedValue = row.custom_attributes.find(
                    (customAttribute: { name: string }) => customAttribute.name === colNameTrimmed,
                )?.value || '';
            } else {
                formattedValue = row[snakeCaseColName] ?? '';
            }

            formattedRow.values.push(formattedValue);
        });
        return formattedRow;
    });

    const sortedData = selectedColumnsData.sort((a, b) => {
        if (!colSortConfig) {
            return 0;
        }
        const { key, direction } = colSortConfig;

        const columnIndex = selectedColumns.indexOf(key);

        if (columnIndex === -1) return 0;

        const aValue = a.values[columnIndex];
        const bValue = b.values[columnIndex];

        if (direction === SortDirection.ASC) {
            return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
    });

    return sortedData;
};

const specialCases: Record<string, string> = {
    Name: 'deal_name',
    'Date created': 'created_at',
    'Investor - LP': 'investor_lp',
    'Lender (private)': 'lender_private',
    'Environmental consultant': 'environment_consultant',
    'IRR (using project actuals)': 'irr_using_project_actuals',
    'Equity multiple (using project actuals)': 'equity_multiple_using_project_actuals',
};

export const convertColNameToSnakeCaseKeys = (str: string) => {
    const snakeKey = specialCases[str] || sentenceToSnakeCase(str);
    return snakeKey;
};
